import { createEffect, createSignal, For, Show } from 'solid-js'
import { Cross, SkipBack, SkipForward } from '@/icons'
import { ensureMathJax, renderTexToSvg } from '@/utils/mathjax'
import { renderMarkdown } from '@/utils/renderMarkdown'
import { sanitizeRichHtml } from '@/utils/sanitizeHtml'
import ui from './TutorialModal.module.css'

export interface TutorialPage {
  title: string
  content: string
}

/**
 * Decode the HTML entities `renderMarkdown` puts in `data-tex` so MathJax gets
 * the original TeX (e.g. `a < b`, not `a &lt; b`). `&amp;` is decoded last so
 * `&amp;lt;` round-trips to `&lt;` rather than `<`.
 */
function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
}

interface TutorialModalProps {
  title: string
  pages: TutorialPage[]
  respond: () => void
}

export function TutorialModal(props: TutorialModalProps) {
  const [currentPage, setCurrentPage] = createSignal(0)
  const [mathJaxLoaded, setMathJaxLoaded] = createSignal(false)
  const totalPages = () => props.pages.length
  const isFirstPage = () => currentPage() === 0
  const isLastPage = () => currentPage() >= totalPages() - 1

  createEffect(() => {
    ensureMathJax()
      .then(() => setMathJaxLoaded(true))
      .catch(() => {})
  })

  function goNext() {
    if (!isLastPage()) setCurrentPage((p) => p + 1)
  }

  function goPrev() {
    if (!isFirstPage()) setCurrentPage((p) => p - 1)
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      goPrev()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      goNext()
    }
  }

  // Render markdown + MathJAX for current page.
  //
  // Order matters: sanitize the markdown shell FIRST, then inject the MathJax
  // SVG into the placeholders. MathJax output must never pass through DOMPurify —
  // it strips the <svg> wrapper, viewBox and glyph <defs>, leaving orphaned paths
  // that collapse the equation to a thin line. The SVG is trusted (generated by
  // MathJax from app-authored tutorial TeX, not user input), so it's safe to
  // inject as-is — the same approach the docs MathSvg uses.
  const renderedContent = () => {
    const pageIdx = currentPage()
    const page = props.pages[pageIdx]
    if (!page) return ''

    const safeHtml = sanitizeRichHtml(renderMarkdown(page.content))
    if (!mathJaxLoaded()) return safeHtml

    return safeHtml
      .replace(
        /<div class="math-block" data-tex="([^"]*)">[\s\S]*?<\/div>/g,
        (_m: string, tex: string) =>
          `<div class="math-block">${renderTexToSvg(decodeEntities(tex)) ?? tex}</div>`,
      )
      .replace(
        /<span class="math-inline" data-tex="([^"]*)">[\s\S]*?<\/span>/g,
        (_m: string, tex: string) =>
          `<span class="math-inline">${renderTexToSvg(decodeEntities(tex), false) ?? tex}</span>`,
      )
  }

  return (
    <div
      class={ui.root}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-title"
      onKeyDown={onKeyDown}
    >
      <div class={ui.header}>
        <h1 id="tutorial-title" class={ui.title}>
          {props.title}
        </h1>
        <button
          class={ui.closeBtn}
          onClick={() => {
            props.respond()
          }}
          aria-label="Close"
          title="Close"
        >
          <Cross width="0.875rem" />
        </button>
      </div>

      <div class={ui.content}>
        <Show
          when={props.pages[currentPage()]}
          keyed
          fallback={<p>No content</p>}
        >
          {(page) => (
            <>
              <h2 class={ui.pageTitle}>{page.title}</h2>
              {/* renderedContent already sanitizes the markdown shell and
                  injects trusted MathJax SVG; do not re-sanitize (DOMPurify
                  would strip the SVG). */}
              <div class={ui.markdownBody} innerHTML={renderedContent()} />
            </>
          )}
        </Show>
      </div>

      <div class={ui.nav}>
        <button
          class={ui.navButton}
          disabled={isFirstPage()}
          onClick={goPrev}
          title="Previous page (Left arrow)"
        >
          <SkipBack width="0.75rem" />
          Previous
        </button>

        <div class={ui.pageIndicator}>
          <For each={Array.from({ length: totalPages() })}>
            {(_, i) => (
              <button
                class={ui.dot}
                classList={{ [ui.dotActive as string]: i() === currentPage() }}
                onClick={() => setCurrentPage(i())}
                title={`Go to page ${i() + 1}`}
              />
            )}
          </For>
        </div>

        <button
          class={ui.navButton}
          disabled={isLastPage()}
          onClick={goNext}
          title={
            isLastPage() ? 'This is the last page' : 'Next page (Right arrow)'
          }
        >
          {isLastPage() ? 'Finish' : 'Next'}
          <SkipForward width="0.75rem" />
        </button>
      </div>
    </div>
  )
}
